"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useAuthStore } from "@/store/auth.store";

interface AppEnv {
  /**
   * Whether the request is authenticated. Seeded from the server token so it is
   * IDENTICAL on the server and on the client's first render — which keeps any
   * query key built from it stable across hydration. Reconciled to the client
   * auth store after mount (in-session login/logout).
   */
  authed: boolean;
}

// Default for trees not wrapped by the provider (graceful, unauthenticated).
const AppEnvContext = createContext<AppEnv>({ authed: false });

export function AppEnvProvider({
  initialAuthed,
  children,
}: {
  initialAuthed: boolean;
  children: ReactNode;
}) {
  const user = useAuthStore((s) => s.user);
  // First render uses the server-seeded value so it matches SSR exactly. Once
  // hydrated, switch to the live client store so an in-session login/logout
  // updates the query keys.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    // Canonical "mounted" flag — set once after hydration.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHydrated(true);
  }, []);
  const authed = hydrated ? !!user : initialAuthed;

  return <AppEnvContext.Provider value={{ authed }}>{children}</AppEnvContext.Provider>;
}

/** Server/client-consistent app environment (auth state) for building stable query keys. */
export function useAppEnv(): AppEnv {
  return useContext(AppEnvContext);
}
